import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  ModelWarehouseInventoryState,
  useSetInventoryProductCountMutation,
  useUpdateWarehouseInventoryProductsMutation,
  type ModelInventoryChange,
  type SetInventoryProductCountRequest,
} from '@app/api/WarehouseInventories';
import { useUserPrint } from '@app/hooks';
import {
  DEFAULT_CONDITION_LABELS,
  dropdownItems,
  getInventoryProductsColumns,
  IN_PROGRESS_CONDITION_LABELS,
  initialPagination,
} from '@app/pages/Warehouses/Inventory/components/InventoryCard/constants';
import { useInventoryWs } from '@app/pages/Warehouses/Inventory/components/InventoryCard/hooks';
import {
  type ConditionOption,
  type HistoryItem,
  type InventoryProduct,
  type OnChangeFiltersType,
  type Pagination,
  type InventoryCardFilters,
  DefaultConditionEnum,
  InProgressConditionEnum,
  SortByEnum,
} from '@app/pages/Warehouses/Inventory/components/InventoryCard/types';
import { useInventoryCardContext } from '@app/providers/InventoryCardProvider/useInventoryCardContext';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { useErrorHandler } from '@app/shared/hooks';
import { bigNumberToString } from '@app/utils/bigNumberToString';
import { clog } from '@app/utils/clog';

import type { PaginationProps } from 'antd';
import type { MenuProps } from 'antd/lib';

export const useInventoryCard = () => {
  const {
    isCompleteInventoryLoading,
    inventory,
    inventoryReport,
    inventoryChanges,
    inventoryProducts,
    isPageLoading,
    refetchInventoryProducts,
    setIsFinishButtonDisabled,
    setCompleteDisabledTooltip,
  } = useInventoryCardContext();

  const {
    params: { ActiveBranchId },
  } = useUserPrint();
  const { inventoryId: inventoryIdQuery } = useParams<{
    inventoryId: string;
  }>();

  const inventoryId = useMemo(() => {
    return inventoryIdQuery && !isNaN(Number(inventoryIdQuery))
      ? Number(inventoryIdQuery)
      : 0;
  }, [inventoryIdQuery]);

  const { showNotification } = useNotification();

  const productCountDraftsRef = useRef<
    Record<number, number | null | undefined>
  >({});
  const [productCountDrafts, setProductCountDrafts] = useState<
    Record<number, number | null | undefined>
  >({});
  const commitInFlightRef = useRef<Set<number>>(new Set());

  const onProductCountChange = useCallback(
    (id: number, value: number | null) => {
      productCountDraftsRef.current[id] = value;

      setProductCountDrafts((prev) => ({
        ...prev,
        [id]: value,
      }));
    },
    []
  );

  const getProductCountValue = useCallback(
    (id: number, serverValue: number | null) => {
      const draftValue = productCountDrafts[id];
      return draftValue !== undefined ? draftValue : serverValue;
    },
    [productCountDrafts]
  );

  const status = useMemo(() => {
    if (!inventory) return null;

    return inventory.State;
  }, [inventory]);

  const historyContainerRef = useRef<HTMLDivElement | null>(null);
  const inProgress = status === ModelWarehouseInventoryState.Open;
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [pagination, setPagination] = useState<Pagination>(initialPagination);
  const [filters, setFilters] = useState<InventoryCardFilters<typeof status>>(
    () => ({
      search: '',
      condition: inProgress
        ? InProgressConditionEnum.ALL
        : DefaultConditionEnum.ALL,
      sortBy: SortByEnum.NAME,
    })
  );

  const [
    updateInventoryProducts,
    {
      isLoading: isUpdateInventoryProductsLoading,
      isError: isUpdateInventoryProductsError,
      error: updateInventoryProductsErrorData,
    },
  ] = useUpdateWarehouseInventoryProductsMutation();

  const [
    setInventoryProductCount,
    {
      isLoading: isSetInventoryProductCountLoading,
      isError: isSetInventoryProductCountError,
      error: setInventoryProductCountErrorData,
    },
  ] = useSetInventoryProductCountMutation();

  const onHistory = useCallback(
    (changes: ModelInventoryChange) => {
      setHistory((prev) => {
        const isExistItem =
          prev?.find(
            (item) =>
              item?.id === changes?.Id &&
              item?.counted === changes?.Count &&
              item?.productName === changes?.Product &&
              item?.timestamp === changes?.Timestamp
          ) !== undefined;

        if (isExistItem) {
          return prev;
        } else {
          const newArray = [
            {
              id: changes?.Id || 0,
              productName: changes?.Product || '',
              counted: changes?.Count || 0,
              timestamp: changes?.Timestamp || '',
            },
            ...prev,
          ];

          return newArray;
        }
      });
    },
    [setHistory]
  );

  useInventoryWs({ onHistory, inventoryId });

  useErrorHandler([
    {
      flag: isUpdateInventoryProductsError,
      error: updateInventoryProductsErrorData,
    },
    {
      flag: isSetInventoryProductCountError,
      error: setInventoryProductCountErrorData,
    },
  ]);

  const setProductCount = useCallback(
    async (data: SetInventoryProductCountRequest['data']): Promise<boolean> => {
      if (!inventoryId) return false;

      try {
        await setInventoryProductCount({
          ActiveBranchId,
          data,
        }).unwrap();

        showNotification('Количество товара успешно обновлено', 'success');
        await refetchInventoryProducts();
        return true;
      } catch (error) {
        showNotification(error, 'warning');
        return false;
      }
    },
    [
      inventoryId,
      showNotification,
      ActiveBranchId,
      setInventoryProductCount,
      refetchInventoryProducts,
    ]
  );

  const commitProductCount = useCallback(
    async (id: number, serverValue: number | null) => {
      if (!inventoryId) return;
      const draftValue = productCountDraftsRef.current[id];
      if (draftValue === undefined) return;
      if (commitInFlightRef.current.has(id)) return;
      if (draftValue === serverValue) {
        setProductCountDrafts((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
        delete productCountDraftsRef.current[id];
        return;
      }

      commitInFlightRef.current.add(id);

      try {
        const ok = await setProductCount({ Id: id, Count: draftValue });

        if (ok) {
          setProductCountDrafts((prev) => {
            const next = { ...prev };
            delete next[id];
            return next;
          });
          delete productCountDraftsRef.current[id];
        }
      } finally {
        commitInFlightRef.current.delete(id);
      }
    },
    [inventoryId, setProductCount]
  );

  const fullData: InventoryProduct[] = useMemo(() => {
    if (inProgress) {
      if (!inventoryProducts) {
        return [];
      }

      return inventoryProducts.map(
        ({ Product, Id, CountWarehouse, CountInventory }) => {
          const countWarehouse = CountWarehouse ?? 0;
          const countInventory = CountInventory ?? null;

          const difference =
            typeof countInventory === 'number'
              ? countInventory - countWarehouse
              : null;

          return {
            id: Id,
            name: Product ?? '',
            countWarehouse,
            countInventory,
            countDifference: 0,
            countDifferencePercent: 0,
            costDifference: 0,
            difference,
          };
        }
      );
    }

    if (!inventoryReport?.Products) {
      return [];
    }

    return inventoryReport.Products.map(
      ({
        CountWarehouse,
        CountInventory,
        CountDifference,
        CostDifference,
        TotalLeftover,
        PhysicalLeftover,
        CreditLeftover,
        HasCreditResidual,
        Unit,
        Id,
        Product,
      }) => {
        const countWarehouse = CountWarehouse ?? 0;
        const countInventory = CountInventory ?? 0;
        const countDifference = CountDifference ?? 0;

        const countDifferencePercent =
          countWarehouse !== 0 ? (countDifference / countWarehouse) * 100 : 0;

        const costDifference = bigNumberToString(CostDifference);

        const difference =
          countInventory !== null && countWarehouse !== null
            ? countInventory - countWarehouse
            : null;

        return {
          id: Id,
          name: Product ?? '',
          countWarehouse,
          countInventory,
          countDifference,
          countDifferencePercent,
          costDifference,
          difference,
          leftover: {
            amount: TotalLeftover,
            physicalAmount: PhysicalLeftover,
            creditAmount: CreditLeftover,
            hasCreditResidual: HasCreditResidual,
            unit: Unit,
          },
        };
      }
    );
  }, [inventoryReport, inProgress, inventoryProducts]);

  const isNonNegativeInteger = useCallback(
    (value: unknown): value is number => {
      return (
        typeof value === 'number' &&
        Number.isFinite(value) &&
        Number.isInteger(value) &&
        value >= 0
      );
    },
    []
  );

  const hasUnfilledCounts = useMemo(() => {
    if (!inProgress) return false;

    return fullData.some((product) => {
      const effectiveValue = getProductCountValue(
        product.id,
        product.countInventory ?? null
      );

      return effectiveValue === null || effectiveValue === undefined;
    });
  }, [inProgress, fullData, getProductCountValue]);

  const hasInvalidCounts = useMemo(() => {
    if (!inProgress) return false;

    return fullData.some((product) => {
      const effectiveValue = getProductCountValue(
        product.id,
        product.countInventory ?? null
      );

      if (effectiveValue === null || effectiveValue === undefined) return false;

      return !isNonNegativeInteger(effectiveValue);
    });
  }, [inProgress, fullData, getProductCountValue, isNonNegativeInteger]);

  const isCompleteDisabled = useMemo(() => {
    if (!inProgress) return false;
    return hasUnfilledCounts || hasInvalidCounts;
  }, [inProgress, hasUnfilledCounts, hasInvalidCounts]);

  const completeDisabledTooltip = useMemo(() => {
    if (!inProgress) return '';
    if (hasUnfilledCounts) return 'Сначала укажите количество для всех товаров';
    if (hasInvalidCounts)
      return 'Введите целое количество ≥ 0 для всех товаров';
    return '';
  }, [inProgress, hasUnfilledCounts, hasInvalidCounts]);

  const data: InventoryProduct[] = useMemo(() => {
    const { search = '' } = filters;

    const filteredData = fullData
      .filter((item) => {
        switch (filters.condition) {
          case InProgressConditionEnum.ALL:
            return true;
          case InProgressConditionEnum.COUNTED:
            return item.difference === 0;
          case InProgressConditionEnum.NOT_COUNTED:
            return item.difference !== 0;
          case DefaultConditionEnum.EXCESS:
            return typeof item.difference === 'number' && item.difference > 0;
          case DefaultConditionEnum.DEFICIENCY:
            return typeof item.difference === 'number' && item.difference < 0;
          case DefaultConditionEnum.NORMAL:
            return typeof item.difference === 'number' && item.difference === 0;
          case DefaultConditionEnum.ALL:
            return true;
          default:
            return true;
        }
      })
      .filter((item) => {
        const searchMatch = item.name
          .toLowerCase()
          .includes(search?.toLowerCase());
        return searchMatch;
      });

    const sortedData = filteredData.sort((a, b) => {
      switch (filters.sortBy) {
        case SortByEnum.NAME:
          return a.name.localeCompare(b.name);
        case SortByEnum.ALL_COUNT:
          return (b?.countWarehouse ?? 0) - (a?.countWarehouse ?? 0);
        case SortByEnum.COUNTED:
          return (b?.countInventory ?? 0) - (a?.countInventory ?? 0);
        default:
          return 0;
      }
    });

    return sortedData;
  }, [fullData, filters]);

  const onDropdownClick = useCallback(
    (id: number): MenuProps['onClick'] =>
      ({ key }) => {
        clog(`Dropdown item clicked: ${key} for product ID: ${id}`);
      },
    []
  );

  const columns = useMemo(
    () =>
      getInventoryProductsColumns({
        inProgress,
        dropdownItems,
        onDropdownClick,
        getProductCountValue,
        onProductCountChange,
        commitProductCount,
        status,
      }),
    [
      onDropdownClick,
      inProgress,
      getProductCountValue,
      onProductCountChange,
      commitProductCount,
      status,
    ]
  );

  const conditionOptions: ConditionOption[] = useMemo(() => {
    switch (status) {
      case ModelWarehouseInventoryState.Open:
        return Object.entries(IN_PROGRESS_CONDITION_LABELS).map(
          ([value, label]) => ({
            value: value as InProgressConditionEnum,
            label,
          })
        );
      default:
        return Object.entries(DEFAULT_CONDITION_LABELS).map(
          ([value, label]) => ({
            value: value as DefaultConditionEnum,
            label,
          })
        );
    }
  }, [status]);

  const isFetching = useMemo(() => {
    return (
      isCompleteInventoryLoading ||
      isUpdateInventoryProductsLoading ||
      isSetInventoryProductCountLoading
    );
  }, [
    isCompleteInventoryLoading,
    isUpdateInventoryProductsLoading,
    isSetInventoryProductCountLoading,
  ]);

  const date = useMemo(() => {
    if (!inventoryReport) return null;

    const { Inventory } = inventoryReport;

    const date = moment(Inventory?.TimeCreated);

    return date.isValid() ? date.format('DD.MM.YYYY HH:mm') : null;
  }, [inventoryReport]);

  const warehouseName = useMemo(() => {
    if (!inventoryReport) return null;

    const { Inventory } = inventoryReport;

    return Inventory?.Warehouse || null;
  }, [inventoryReport]);

  const comment = useMemo(() => {
    if (!inventoryReport) return null;

    const { Inventory } = inventoryReport;

    return Inventory?.Comment || null;
  }, [inventoryReport]);

  const onChangeFilters: OnChangeFiltersType = useCallback((key, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [key]: value,
    }));
  }, []);

  const onPaginate: PaginationProps['onChange'] = useCallback(
    (page: number) => {
      setPagination((prevPagination) => ({ ...prevPagination, page }));
    },
    []
  );

  const onPageSizeChange = useCallback((itemsPerPage: number) => {
    setPagination((prevPagination) => ({
      ...prevPagination,
      itemsPerPage,
      page: 1,
    }));
  }, []);

  const updateProducts = useCallback(async () => {
    if (!inventoryId) return;

    try {
      await updateInventoryProducts({
        ActiveBranchId,
        inventoryId,
      }).unwrap();

      showNotification('Товары инвентаризации успешно обновлены', 'success');
    } catch (error) {
      showNotification(error, 'warning');
    }
  }, [inventoryId, showNotification, ActiveBranchId, updateInventoryProducts]);

  const fetchedHistoryHandler = useCallback(() => {
    if (!inProgress || !inventoryChanges) {
      setHistory([]);
    }

    setHistory(
      inventoryChanges?.map(({ Id, Product, Count, Timestamp }) => ({
        id: Id,
        productName: Product ?? '',
        counted: Count ?? 0,
        timestamp: Timestamp ?? '',
      })) || []
    );
  }, [inProgress, inventoryChanges]);

  const scrollHistoryToBottom = useCallback(() => {
    if (history.length > 0 && historyContainerRef.current) {
      const container = historyContainerRef.current;
      container.scrollTop = container.scrollHeight;
    }
  }, [history.length]);

  const isFinishButtonDisabled =
    inProgress && (isCompleteDisabled || isFetching);

  useEffect(() => {
    setIsFinishButtonDisabled(isFinishButtonDisabled);
  }, [isFinishButtonDisabled, setIsFinishButtonDisabled]);

  useEffect(() => {
    setCompleteDisabledTooltip(completeDisabledTooltip);
  }, [completeDisabledTooltip, setCompleteDisabledTooltip]);

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      from: data.length > 0 ? (prev.pageN - 1) * prev.itemsPerPage + 1 : 0,
      to: Math.min(prev.pageN * prev.itemsPerPage, data.length),
      total: data.length,
    }));
  }, [data]);

  return {
    updateProducts,
    setProductCount,
    inProgress,
    conditionOptions,
    filters,
    onChangeFilters,
    isPageLoading,
    inventoryReport,
    date,
    warehouseName,
    comment,
    isFetching,
    inventoryChanges,
    inventoryProducts,
    inventory,
    data,
    columns,
    pagination,
    onPaginate,
    onPageSizeChange,
    history,
    status,
    fetchedHistoryHandler,
    scrollHistoryToBottom,
    historyContainerRef,
    isCompleteDisabled,
    completeDisabledTooltip,
  };
};
